/**
 * The closed accent allowlist, and a machine-readable mirror of the colours in
 * `src/styles/tokens.css`.
 *
 * **A user picks a name, never a colour.** That is the whole safety property, taken from `emrx`'s
 * `THEMEABLE_TOKENS` (`docs/comparison-emrx.md` §5.1 — "a theme is data, and data can be wrong"):
 * because the set of reachable values is finite and every one of them has been measured, no
 * preference a user can express can make their own charts illegible. The lightness of each pair is
 * system-controlled; only the hue family is chosen.
 *
 * This is not the same as the tenant-branding requirement in `claude-kit/rules/invariants.md`
 * ("Commercial products let the customer brand it, within limits"). A practice supplying an
 * arbitrary brand hex is a later feature, and it is exactly the case that needs the runtime guard
 * in `guard.ts` rather than an allowlist, because its input cannot be enumerated in advance.
 *
 * The colour values below duplicate `tokens.css` on purpose, and `palette.test.ts` reads that CSS
 * file and asserts the two agree — so the duplication cannot drift silently, and the contrast
 * assertions in the same file are therefore assertions about what actually ships.
 */

/** A resolved theme. `'system'` is a *preference*, not a mode — see `preference.ts`. */
export const THEME_MODES = ['light', 'dark'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

/**
 * Every accent a user may choose. Adding one means adding a `:root[data-accent='…']` block to
 * `tokens.css` *and* a row to `ACCENT_COLORS`; the test fails on either half being missing, which
 * is the forcing function.
 */
export const ACCENTS = ['blue', 'teal', 'violet', 'amber', 'rose', 'green'] as const;
export type Accent = (typeof ACCENTS)[number];

/** Southwest EMR's brand accent (`emr-po/branding/`) — see `tokens.css`'s teal block header for
 * how its exact hex values were derived from the source palette. */
export const DEFAULT_ACCENT: Accent = 'teal';

/** Human-readable names for the picker. Not colour words a screen-reader user cannot verify. */
export const ACCENT_LABELS: Record<Accent, string> = {
  blue: 'Blue',
  teal: 'Teal',
  violet: 'Violet',
  amber: 'Amber',
  rose: 'Rose',
  green: 'Green',
};

export function isAccent(value: string): value is Accent {
  return (ACCENTS as readonly string[]).includes(value);
}

export interface AccentSwatch {
  /** Filled control background. */
  readonly solid: string;
  /** Text and icons drawn on `solid`. */
  readonly onSolid: string;
  /** The accent used as text or an icon directly on a page surface. */
  readonly text: string;
  /** A tinted block — active navigation, selected rows. */
  readonly soft: string;
}

export interface SurfaceSet {
  readonly canvas: string;
  readonly surface: string;
  readonly raised: string;
  readonly ink: string;
  readonly inkMuted: string;
  readonly line: string;
  readonly lineStrong: string;
}

/**
 * Mirrors the tier-2 surface mappings in `tokens.css`. The "Southwest EMR" desert palette from
 * `emr-po/branding/` — applied here per `BACKLOG.md` B4a, replacing the prior blue-grey neutrals.
 * Every pairing is re-measured in `palette.test.ts` against the actual thresholds, not carried over
 * from the source HTML swatches by eye.
 */
export const SURFACES: Record<ThemeMode, SurfaceSet> = {
  light: {
    canvas: '#fbf8f3',
    surface: '#fffefb',
    raised: '#f1e9dc',
    ink: '#2a1b12',
    inkMuted: '#6e5b49',
    line: '#e4d8c4',
    lineStrong: '#8a6f55',
  },
  dark: {
    canvas: '#181210',
    surface: '#221a15',
    raised: '#2c2119',
    ink: '#f5eee3',
    inkMuted: '#c9b8a5',
    line: '#3a2a1e',
    lineStrong: '#8a7361',
  },
};

/** Mirrors the `--p-accent-*` blocks in `tokens.css`. */
export const ACCENT_COLORS: Record<Accent, Record<ThemeMode, AccentSwatch>> = {
  blue: {
    light: { solid: '#1d4ed8', onSolid: '#ffffff', text: '#1d4ed8', soft: '#e8eefc' },
    dark: { solid: '#7cb2fb', onSolid: '#0b1017', text: '#8fc0ff', soft: '#17233a' },
  },
  teal: {
    light: { solid: '#1c716b', onSolid: '#ffffff', text: '#1c716b', soft: '#ddf1ee' },
    dark: { solid: '#5fcfc7', onSolid: '#0b1017', text: '#6fe0cf', soft: '#123330' },
  },
  violet: {
    light: { solid: '#6d28d9', onSolid: '#ffffff', text: '#6d28d9', soft: '#efe8fd' },
    dark: { solid: '#bda2fb', onSolid: '#0b1017', text: '#c8b2ff', soft: '#241c3d' },
  },
  amber: {
    light: { solid: '#9a3412', onSolid: '#ffffff', text: '#9a3412', soft: '#fbeddc' },
    dark: { solid: '#f2b45c', onSolid: '#0b1017', text: '#f6c377', soft: '#33260f' },
  },
  rose: {
    light: { solid: '#be123c', onSolid: '#ffffff', text: '#be123c', soft: '#fde8ed' },
    dark: { solid: '#fb9db1', onSolid: '#0b1017', text: '#ffadbf', soft: '#341922' },
  },
  green: {
    light: { solid: '#146c33', onSolid: '#ffffff', text: '#146c33', soft: '#e3f5e9' },
    dark: { solid: '#5fd07f', onSolid: '#0b1017', text: '#74dc92', soft: '#12301e' },
  },
};

export interface StatusSet {
  readonly danger: string;
  readonly dangerInk: string;
  readonly warning: string;
  readonly success: string;
}

/**
 * Status colours are **not** accent-dependent, deliberately. A danger colour a tenant could
 * re-brand is a patient-safety problem, not a theming feature.
 */
export const STATUS: Record<ThemeMode, StatusSet> = {
  light: { danger: '#b42318', dangerInk: '#ffffff', warning: '#8a5300', success: '#146c33' },
  dark: { danger: '#ff9d94', dangerInk: '#0b1017', warning: '#e8b35a', success: '#6ed58c' },
};

export interface NoticeSet {
  readonly dangerBg: string;
  readonly dangerInk: string;
  readonly infoBg: string;
  readonly infoInk: string;
}

/**
 * The environment banner (`shared/src/environmentBadge.ts`), which is a safety control rather
 * than styling: it is what distinguishes a demo from a subscriber test environment holding real
 * patient data. Accent-independent, so no user preference and no future tenant brand colour can
 * quieten it, and asserted in `palette.test.ts` at AA like everything else.
 */
export const NOTICE: Record<ThemeMode, NoticeSet> = {
  light: {
    dangerBg: '#b42318',
    dangerInk: '#ffffff',
    infoBg: '#14181f',
    infoInk: '#ffffff',
  },
  dark: {
    dangerBg: '#ff9d94',
    dangerInk: '#0b1017',
    infoBg: '#e6edf3',
    infoInk: '#0b1017',
  },
};
